import { retry, shareAsyncAction, cached } from '../../async';

import { isAPISupported, retryFBOperation, isUserInputError, isNetworkFailureError } from './FBHelpers';
import { IAPCatalog } from './IAPCatalog';
import { report } from '../../../report';
import { track } from 'Utility/Tracking/Track';

export class FBPayments {
    public readonly iapCatalog: IAPCatalog = { products: [] };

    public readonly hasIAPSupport = () => false;

    public async purchaseProduct(productId: string): Promise<FBInstant.Purchase> {
        let purchase: FBInstant.Purchase;
        try {
            // Not retrying this on purpose
            purchase = await FBInstant.payments.purchaseAsync({
                productID: productId,
            });
        } catch (error) {
            report('purchaseAsync failed', {
                error,
                tags: {
                    productID: productId,
                    message: error.message,
                    code: error.code,
                },
                sampleRate: isUserInputError(error) || isNetworkFailureError(error) ? 0 : 1,
                fingerprint: 'PurchaseAsyncFailed:purchaseProduct',
            });
            throw error;
        }
        track('transactionID', purchase.paymentID);
        //await this.consumePurchase(purchase, true);
        return purchase;
    }

    public readonly fetchCatalog = cached(async () => {
        // SOFA-2303
        // await this.waitForPaymentsEnabled();
        let catalog = new Array<FBInstant.Product>();
        try {
            catalog = await retryFBOperation('getCatalogAsync', () => FBInstant.payments.getCatalogAsync(), undefined)
                .promise;
            if (!catalog) return false;
        } catch (error) {
            report('getCatalogAsync failed', {
                error,
                once: true,
                tags: {
                    message: error.message,
                    code: error.code,
                },
                sampleRate: isNetworkFailureError(error) ? 0 : 0.5,
                fingerprint: 'FailedToGetCatalog:fetchCatalog',
            });
            return false;
        }
        try {
            this.iapCatalog.products.push(
                ...catalog.map(entry => ({
                    productId: entry.productID,
                    price: entry.price,
                    priceCurrencyCode: entry.priceCurrencyCode,
                }))
            );
        } catch (error) {
            report('Failed to parse catalog', {
                error,
                once: true,
                sampleRate: 0.5,
                tags: {
                    message: error.message,
                    code: error.code,
                },
                fingerprint: 'FailedToParseCatalog:fetchCatalog',
            });
            return false;
        }
        return true;
    });

    public readonly waitForPaymentsEnabled = cached<Promise<boolean>>(async () => {
        if (!this.hasIAPSupport()) {
            return false;
        }

        return new Promise(resolve => {
            FBInstant.payments.onReady(() => {
                setTimeout(() => resolve(true), 5000);
                // resolve(true);
            });
        });
    });

    /**
     * Check for non-consumed purchases and consume them.
     * Ideally this should not happen.
     * Purchases should be consumed and applied immediately after purchasing.
     * However, if any unconsumed/stale purchases exist,
     * then the player will not be able to re-purchase the product.
     */
    public readonly resolveUnconsumedPurchases = shareAsyncAction(async () => {
        let purchases = await this.getPurchases();
        try {
            if (purchases.length > 0) {
                const tags = {
                    productIDs: purchases.map(p => p.productID).join(', '),
                    developerPayloads: purchases.map(p => p.developerPayload).join(', '),
                    purchaseTimes: purchases.map(p => p.purchaseTime).join(', '),
                };
                report('Unconsumed purchases detected', {
                    tags,
                    fingerprint: 'resolveUnconsumedPurchases:UnconsumedPurchasesDetected',
                });
            }
        } catch (error) {
            report('Failed to resolve unconsumed purchases', {
                error,
                sampleRate: isNetworkFailureError(error) ? 0 : 1,
                tags: {
                    message: error.message,
                    code: error.code,
                },
                fingerprint: 'resolveUnconsumedPurchases:FailedToResolve',
            });
        }
        return purchases;
    });

    public readonly getPurchases = shareAsyncAction(async () => {
        let purchases = new Array<FBInstant.Purchase>();
        try {
            purchases = await retryFBOperation(
                'getPurchasesAsync',
                () => FBInstant.payments.getPurchasesAsync(),
                purchases
            ).promise;
        } catch (error) {
            report('Failed to getPurchasesAsync', {
                error,
                sampleRate: isNetworkFailureError(error) ? 0 : 1,
                tags: {
                    message: error.message,
                    code: error.code,
                },
                fingerprint: 'getPurchases:FailedToGetPurchases',
            });
        }
        return purchases;
    });

    public async consumePurchase(purchase: FBInstant.Purchase, propagateError: boolean = false): Promise<boolean> {
        try {
            await retry(() => FBInstant.payments.consumePurchaseAsync(purchase.purchaseToken), {
                onError: error => {
                    report(`consumePurchaseAsync failed`, {
                        error,
                        once: true,
                        sampleRate: 0.1,
                        fingerprint: 'consumePurchase:FailedToConsumePurchase:onRetryError',
                        tags: {
                            productId: purchase.productID,
                        },
                    });
                },
            }).promise;
            return true;
        } catch (error) {
            report('Failed to consumePurchaseAsync', {
                error,
                sampleRate: isNetworkFailureError(error) ? 0 : 1,
                tags: {
                    developerPayload: purchase.developerPayload,
                    productID: purchase.productID,
                    purchaseTime: purchase.purchaseTime,
                    message: error.message,
                    code: error.code,
                },
                fingerprint: 'consumePurchase:FailedToConsumePurchase',
            });
            if (propagateError) throw error;
        }
        return false;
    }
}
